import type { Conversation, Message } from '@twilio/conversations';

import {
  RESOLVED_CONVERSATION_MESSAGE_TEXT,
  getConversationAttributes,
  isResolvedConversationMessage,
  isVisibleConversationMessage,
} from '@south-street-app/utils';

type ChatMessageSide = 'left' | 'right' | 'system';

type ChatMessageViewModel = {
  id: string;
  side: ChatMessageSide;
  text: string;
  timestampLabel?: string;
  createdAt?: Date | string | number | null;
  authorId?: string;
  avatarLabel?: string;
};

type ChatInboxListRow = {
  id: 'support' | 'updates';
  kind: 'support' | 'updates';
  title: string;
  subtitle: string;
  timestamp?: Date | null;
  unreadCount: number;
  selected: boolean;
};

const RESOLVED_MESSAGE_ID = 'resolved-indicator';
const CHAT_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});
const CHAT_DAY_WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
});
const CHAT_DAY_MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
});
const CHAT_DAY_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
});

const isSameCalendarDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const formatChatDayTime = (date: Date) =>
  CHAT_TIME_FORMATTER.format(date).replace(' ', '').toLowerCase();

const formatChatTimestampLabel = (
  createdAt: Date | string | number | null | undefined,
) => {
  if (!createdAt) {
    return '';
  }

  return CHAT_TIME_FORMATTER.format(new Date(createdAt));
};

const formatChatDayDividerLabel = (
  createdAt: Date | string | number | null | undefined,
  now = new Date(),
) => {
  if (createdAt == null) {
    return '';
  }

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const dayLabel = isSameCalendarDay(date, now)
    ? 'Today'
    : CHAT_DAY_WEEKDAY_FORMATTER.format(date);

  return `${dayLabel}, ${CHAT_DAY_MONTH_FORMATTER.format(
    date,
  )} ${CHAT_DAY_DATE_FORMATTER.format(date)} at ${formatChatDayTime(date)}`;
};

const getSortableMessageTime = (message: {
  createdAt?: Date | string | number | null;
  dateCreated?: Date | string | number | null;
}) => new Date(message.createdAt ?? message.dateCreated ?? 0).getTime();

const mergeChatMessages = <
  T extends {
    createdAt?: Date | string | number | null;
    dateCreated?: Date | string | number | null;
  },
>(
  messages: T[],
) =>
  [...messages].sort(
    (left, right) =>
      getSortableMessageTime(right) - getSortableMessageTime(left),
  );

const toChatMessageViewModel = ({
  activeUserId,
  avatarLabel,
  conversation,
  message,
}: {
  activeUserId?: string;
  avatarLabel?: string;
  conversation: Conversation | null;
  message: Message;
}): ChatMessageViewModel | null => {
  const isResolvedMessage = isResolvedConversationMessage(
    message.author,
    message.body,
  );

  if (
    !isVisibleConversationMessage(message.author, message.body, conversation)
  ) {
    return null;
  }

  const side: ChatMessageSide = isResolvedMessage
    ? 'system'
    : message.author === activeUserId
    ? 'right'
    : 'left';

  return {
    id: message.sid,
    side,
    text: isResolvedMessage
      ? RESOLVED_CONVERSATION_MESSAGE_TEXT
      : message.body ?? '',
    timestampLabel: formatChatTimestampLabel(message.dateCreated),
    createdAt: message.dateCreated,
    authorId: message.author ?? '',
    ...(side === 'left' && avatarLabel ? { avatarLabel } : undefined),
  };
};

const withResolvedChatMessage = ({
  conversation,
  messages,
}: {
  conversation: Conversation | null;
  messages: ChatMessageViewModel[];
}) => {
  const attributes = getConversationAttributes(conversation);

  if (attributes.status !== 'resolved') {
    return messages;
  }

  if (
    messages.some(
      (message) =>
        message.side === 'system' &&
        message.text === RESOLVED_CONVERSATION_MESSAGE_TEXT,
    )
  ) {
    return messages;
  }

  return [
    {
      id: RESOLVED_MESSAGE_ID,
      side: 'system' as const,
      text: RESOLVED_CONVERSATION_MESSAGE_TEXT,
      timestampLabel: messages[0]?.timestampLabel ?? '',
      createdAt: messages[0]?.createdAt ?? new Date(),
    },
    ...messages,
  ];
};

export {
  formatChatDayDividerLabel,
  formatChatTimestampLabel,
  mergeChatMessages,
  toChatMessageViewModel,
  withResolvedChatMessage,
};
export type { ChatInboxListRow, ChatMessageSide, ChatMessageViewModel };
